import { useMemo } from "react";
import { useGetFullProject } from "./useGetFullProject";
import { getNestedProperty } from "../utils/nested-properties";

// Default configuration used while project data is loading
// This allows the UI to render immediately without blocking
const DEFAULT_WEB_UX_CONFIG = {
    ux_version: null, // Will render default dashboard (v1)
    banner: {
        enabled: false,
        lg: "",
        md: "",
        sm: "",
        background_image: "",
    },
};

/**
 * Hook to access web_ux_config from project data
 * Provides easy access to UX configuration including:
 * - ux_version
 * - banner configuration
 * - widget configurations
 * - Any other UX-related settings
 *
 * Returns default values while loading to prevent UI blocking
 */
export const useWebUxConfig = () => {
    const { data, isLoading } = useGetFullProject();

    const webUxConfig = useMemo(() => {
        if (!data) {
            return DEFAULT_WEB_UX_CONFIG;
        }

        return (
            getNestedProperty(data, "properties.web_ux_config.value", DEFAULT_WEB_UX_CONFIG) ||
            DEFAULT_WEB_UX_CONFIG
        );
    }, [data]);

    const uxVersion = useMemo(() => {
        return getNestedProperty(webUxConfig, "ux_version", DEFAULT_WEB_UX_CONFIG.ux_version);
    }, [webUxConfig]);

    const bannerConfig = useMemo(() => {
        return (
            getNestedProperty(webUxConfig, "banner", DEFAULT_WEB_UX_CONFIG.banner) ||
            DEFAULT_WEB_UX_CONFIG.banner
        );
    }, [webUxConfig]);

    /**
     * Get a nested property from web_ux_config
     * @param path - Dot-separated path (e.g., "widgets.cards.enabled")
     * @param defaultValue - Default value if path doesn't exist
     */
    const getConfig = <T = any>(path: string, defaultValue: T | null = null) => {
        return getNestedProperty(webUxConfig, path, defaultValue);
    };

    /**
     * Check if a configuration path exists
     * @param path - Dot-separated path
     */
    const hasConfig = (path: string): boolean => {
        return getNestedProperty(webUxConfig, path, null) !== null;
    };

    return {
        webUxConfig,
        uxVersion,
        bannerConfig,
        getConfig,
        hasConfig,
        isLoading,
    };
};
